import { useEffect, useRef } from 'react'
import {
  useTerminal,
  type LogKind,
  type Stage,
  type StageState,
} from '../lib/terminalContext'
import { modelCodename } from '../lib/labCodenames'

// SessionTerminal — dockable "fv.cli" strip fixed at the bottom of the
// viewport. Two visual states:
//
//   Collapsed (32px tall): slim activity bar. Shows the current aggregate
//     status + six stage pips + an expand affordance.
//
//   Expanded (320px tall): two-pane layout. Left pane is a faux script
//     editor that displays an always-visible TypeScript-looking file with
//     the live prompt embedded inside a template literal + a blinking
//     cursor. Right pane is the runtime log — streams fake preflight
//     operations before generation, real outcomes after.
//
// The CSS variable --terminal-height is driven from terminalContext so the
// PromptBar can sit exactly above the terminal without prop drilling.

const STAGES: { key: Stage; label: string }[] = [
  { key: 'compile', label: 'COMP' },
  { key: 'safety', label: 'SAFE' },
  { key: 'route', label: 'ROUTE' },
  { key: 'encode', label: 'ENC' },
  { key: 'diffuse', label: 'DIFF' },
  { key: 'upload', label: 'UP' },
]

export default function SessionTerminal() {
  const {
    expanded,
    toggle,
    promptPreview,
    modelName,
    logs,
    clearLogs,
    status,
  } = useTerminal()

  const logScrollRef = useRef<HTMLDivElement>(null)

  // Auto-scroll the runtime log to the bottom on new entries
  useEffect(() => {
    if (logScrollRef.current) {
      logScrollRef.current.scrollTop = logScrollRef.current.scrollHeight
    }
  }, [logs])

  // High-level status summary shown in the collapsed bar
  const aggregate = summarizeStatus(status)

  return (
    <aside
      className="fixed bottom-0 left-[var(--sidebar-width)] right-0 z-30 border-t border-border bg-bg-secondary/95 backdrop-blur-md max-md:left-0 transition-[height] duration-200 overflow-hidden"
      style={{ height: 'var(--terminal-height, 32px)' }}
    >
      {/* ── Collapsed header (always visible) ─────────────────────────── */}
      <div className="h-8 flex items-center justify-between px-3 border-b border-border/40">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="w-2 h-2 rounded-full bg-error/60" />
            <span className="w-2 h-2 rounded-full bg-warning/60" />
            <span className="w-2 h-2 rounded-full bg-neon/60" />
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-text-muted shrink-0">
            fv.cli
          </span>
          <span className="w-px h-3 bg-border shrink-0" />
          <AggregatePip status={aggregate} />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-text-secondary truncate">
            {aggregateLabel(aggregate)}
          </span>
          <span className="w-px h-3 bg-border shrink-0" />
          {/* Per-stage pips */}
          <div className="flex items-center gap-1.5">
            {STAGES.map((s) => (
              <StagePip key={s.key} label={s.label} state={status[s.key]} />
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {expanded && (
            <button
              onClick={clearLogs}
              className="font-mono text-[9px] uppercase tracking-[0.2em] text-text-muted hover:text-text-primary px-2 py-0.5 rounded cursor-pointer transition-colors"
            >
              clear
            </button>
          )}
          <button
            onClick={toggle}
            className="font-mono text-[10px] text-text-muted hover:text-neon px-2 py-0.5 rounded cursor-pointer transition-colors"
            title={expanded ? 'Collapse terminal' : 'Expand terminal'}
          >
            {expanded ? '⌃ collapse' : '⌄ expand'}
          </button>
        </div>
      </div>

      {/* ── Expanded body: script pane + runtime pane ────────────────── */}
      {expanded && (
        <div className="flex h-[calc(100%-32px)] font-mono text-[11px] leading-[1.6]">
          {/* Script pane */}
          <div className="flex-1 min-w-0 border-r border-border/40 overflow-y-auto p-4 bg-black/40">
            <ScriptHeader filename="frankenvibes.lab.ts" />
            <ScriptEditor promptPreview={promptPreview} modelName={modelName} />
          </div>

          {/* Runtime pane */}
          <div className="w-[40%] min-w-[300px] max-w-[520px] flex flex-col">
            <div className="px-3 py-1.5 border-b border-border/40 bg-bg-elevated/30">
              <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-text-muted">
                / runtime
              </span>
            </div>
            <div
              ref={logScrollRef}
              className="flex-1 overflow-y-auto px-3 py-2 bg-black/60"
            >
              {logs.length === 0 ? (
                <p className="text-text-muted italic">
                  &gt; idle. waiting for generate event...
                </p>
              ) : (
                logs.map((entry) => (
                  <div key={entry.id} className="whitespace-pre flex items-baseline gap-2">
                    <span className={logColor(entry.kind)}>&gt;</span>
                    <span className="text-text-secondary flex-shrink-0">
                      {entry.label.padEnd(32, ' ')}
                    </span>
                    <span className={`${logColor(entry.kind)} font-semibold`}>
                      [{logBadge(entry.kind)}]
                    </span>
                    {entry.note && (
                      <span className="text-text-muted truncate">{entry.note}</span>
                    )}
                  </div>
                ))
              )}
              {/* Blinking cursor at end of the log */}
              <div className="flex items-center">
                <span className="text-neon">▌</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </aside>
  )
}

// ─── Script header ("file tab") ───────────────────────────────────────────
function ScriptHeader({ filename }: { filename: string }) {
  return (
    <div className="flex items-center gap-2 pb-3 border-b border-border/40 mb-3">
      <span className="w-2 h-2 rounded-full bg-neon" />
      <span className="text-text-secondary text-[10px]">{filename}</span>
      <span className="ml-auto font-mono text-[9px] uppercase tracking-[0.2em] text-text-muted">
        / session source
      </span>
    </div>
  )
}

// ─── The script editor body ──────────────────────────────────────────────
function ScriptEditor({
  promptPreview,
  modelName,
}: {
  promptPreview: string
  modelName: string
}) {
  const displayedPrompt = promptPreview.length > 0 ? promptPreview : ''
  // Always show the codename in the script editor so screenshots don't
  // leak the real model/provider. The real name is still visible in the
  // model selector — this is brand flavor, not secrecy.
  const codename = modelCodename(modelName)
  let lineNo = 0
  const L = () => ++lineNo

  return (
    <div className="space-y-0.5">
      {/* Line: import */}
      <ScriptLine n={L()}>
        <C t="kw">import</C> <C t="text">model</C>{' '}
        <C t="punct">{'{'}</C> <C t="type">{codename}</C> <C t="punct">{'}'}</C>{' '}
        <C t="kw">from</C> <C t="str">{'"@core/lanes"'}</C>
      </ScriptLine>
      <ScriptLine n={L()}>
        <C t="kw">import</C> <C t="punct">{'{'}</C>{' '}
        <C t="text">encoder, scheduler, diffuser</C> <C t="punct">{'}'}</C>{' '}
        <C t="kw">from</C> <C t="str">{'"@fv/core"'}</C>
      </ScriptLine>

      <ScriptLine n={L()}></ScriptLine>

      {/* Config block */}
      <ScriptLine n={L()}>
        <C t="kw">const</C> <C t="text">cfg</C> <C t="punct">=</C>{' '}
        <C t="punct">{'{'}</C>
      </ScriptLine>
      <ScriptLine n={L()} indent={2}>
        <C t="text">width</C>
        <C t="punct">:</C> <C t="num">1024</C>
        <C t="punct">,</C>
      </ScriptLine>
      <ScriptLine n={L()} indent={2}>
        <C t="text">height</C>
        <C t="punct">:</C> <C t="num">1024</C>
        <C t="punct">,</C>
      </ScriptLine>
      <ScriptLine n={L()} indent={2}>
        <C t="text">steps</C>
        <C t="punct">:</C> <C t="num">4</C>
        <C t="punct">,</C>
      </ScriptLine>
      <ScriptLine n={L()} indent={2}>
        <C t="text">guidance</C>
        <C t="punct">:</C> <C t="num">3.5</C>
        <C t="punct">,</C>
      </ScriptLine>
      <ScriptLine n={L()}>
        <C t="punct">{'}'}</C>
      </ScriptLine>

      <ScriptLine n={L()}></ScriptLine>

      {/* Prompt block — the live-typing slot */}
      <ScriptLine n={L()}>
        <C t="kw">const</C> <C t="text">prompt</C> <C t="punct">=</C>{' '}
        <C t="str">{'`'}</C>
      </ScriptLine>
      <ScriptLine n={L()} indent={2}>
        {displayedPrompt ? (
          <>
            <C t="str">{displayedPrompt}</C>
            <span className="text-neon animate-pulse">▌</span>
          </>
        ) : (
          <>
            <C t="com">{'// type a prompt to begin'}</C>
            <span className="text-neon animate-pulse">▌</span>
          </>
        )}
      </ScriptLine>
      <ScriptLine n={L()}>
        <C t="str">{'`'}</C>
      </ScriptLine>

      <ScriptLine n={L()}></ScriptLine>

      {/* Function call */}
      <ScriptLine n={L()}>
        <C t="text">fv</C>
        <C t="punct">.</C>
        <C t="fn">generate</C>
        <C t="punct">(</C>
        <C t="text">prompt</C>
        <C t="punct">, </C>
        <C t="text">cfg</C>
        <C t="punct">)</C>
      </ScriptLine>
    </div>
  )
}

// ─── Syntax-colored span ─────────────────────────────────────────────────
type Tok = 'kw' | 'str' | 'num' | 'text' | 'punct' | 'type' | 'com' | 'fn'
function C({ t, children }: { t: Tok; children: React.ReactNode }) {
  const cls =
    t === 'kw'
      ? 'text-neon'
      : t === 'str'
      ? 'text-warning'
      : t === 'num'
      ? 'text-text-primary'
      : t === 'text'
      ? 'text-text-primary'
      : t === 'punct'
      ? 'text-text-secondary'
      : t === 'type'
      ? 'text-neon/80'
      : t === 'com'
      ? 'text-text-muted italic'
      : t === 'fn'
      ? 'text-warning'
      : 'text-text-primary'
  return <span className={cls}>{children}</span>
}

function ScriptLine({
  n,
  indent = 0,
  children,
}: {
  n: number
  indent?: number
  children?: React.ReactNode
}) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-text-muted select-none w-6 text-right shrink-0">{n}</span>
      <span className="flex-1 min-w-0 whitespace-pre-wrap break-words">
        {indent > 0 && <span>{' '.repeat(indent)}</span>}
        {children}
      </span>
    </div>
  )
}

// ─── Pips ─────────────────────────────────────────────────────────────────
function StagePip({ label, state }: { label: string; state: StageState }) {
  const color =
    state === 'ok'
      ? 'bg-neon shadow-[0_0_6px_var(--color-neon-glow)]'
      : state === 'running'
      ? 'bg-warning animate-pulse'
      : state === 'fail'
      ? 'bg-error'
      : 'bg-border'
  return (
    <div className="flex items-center gap-1" title={`${label}: ${state}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${color}`} />
      <span
        className={`font-mono text-[9px] uppercase tracking-wider ${
          state === 'idle' ? 'text-text-muted' : 'text-text-secondary'
        }`}
      >
        {label}
      </span>
    </div>
  )
}

function AggregatePip({ status }: { status: 'idle' | 'running' | 'ok' | 'fail' }) {
  const color =
    status === 'ok'
      ? 'bg-neon shadow-[0_0_6px_var(--color-neon-glow)]'
      : status === 'running'
      ? 'bg-warning animate-pulse'
      : status === 'fail'
      ? 'bg-error animate-pulse'
      : 'bg-text-muted'
  return <span className={`w-2 h-2 rounded-full ${color} shrink-0`} />
}

// ─── Helpers ──────────────────────────────────────────────────────────────
function summarizeStatus(
  s: Record<Stage, StageState>,
): 'idle' | 'running' | 'ok' | 'fail' {
  const vals = Object.values(s)
  if (vals.some((v) => v === 'fail')) return 'fail'
  if (vals.some((v) => v === 'running')) return 'running'
  if (vals.every((v) => v === 'idle')) return 'idle'
  if (vals.every((v) => v === 'ok' || v === 'idle')) return 'ok'
  return 'running'
}

function aggregateLabel(s: 'idle' | 'running' | 'ok' | 'fail'): string {
  switch (s) {
    case 'idle':
      return 'idle'
    case 'running':
      return 'running'
    case 'ok':
      return 'ready'
    case 'fail':
      return 'error'
  }
}

function logColor(k: LogKind): string {
  switch (k) {
    case 'ok':
      return 'text-neon'
    case 'fail':
      return 'text-error'
    case 'running':
      return 'text-warning'
    case 'done':
      return 'text-neon'
    case 'info':
      return 'text-text-secondary'
  }
}

function logBadge(k: LogKind): string {
  switch (k) {
    case 'ok':
      return 'OK'
    case 'fail':
      return 'FAIL'
    case 'running':
      return '...'
    case 'done':
      return 'DONE'
    case 'info':
      return 'INFO'
  }
}
